import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner, Badge, Breadcrumb } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useProductApi, { Product } from '../../managers/ProductManager';
import { TEMPLATE_GROUPS } from '../../managers/TemplateGroupManager';

const TemplateGroup: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { getTemplateProducts } = useProductApi();
  const [templates, setTemplates] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Find the group info
  const group = TEMPLATE_GROUPS.find(g => g.id === groupId);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const allTemplates = await getTemplateProducts();
        // Filter templates by this group (first tag matches groupId)
        const filteredTemplates = allTemplates.filter(t => t.tags?.[0] === groupId);
        setTemplates(filteredTemplates);
      } catch (err) {
        console.error('Error fetching templates for group', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [groupId]);

  if (!group) {
    return (
      <Container className="mt-5 text-center">
        <h2>Template Group Not Found</h2>
        <p className="text-muted">The template group you're looking for doesn't exist.</p>
      </Container>
    );
  }

  const GroupIcon = group.icon;

  return (
    <>
      <Helmet>
        <title>{group.name} Templates - Wallscapes.AI</title>
        <meta name="description" content={`Browse and customize ${group.name.toLowerCase()} templates. ${group.description}`} />
      </Helmet>

      <Container className="mt-3">
        {/* Breadcrumb Navigation */}
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => navigate('/')}>Home</Breadcrumb.Item>
          <Breadcrumb.Item active>{group.name}</Breadcrumb.Item>
        </Breadcrumb>

        {/* Group Header */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
            <GroupIcon size={48} className="text-primary" />
            <h1 className="mb-0">{group.name}</h1>
          </div>
          <p className="lead text-muted">Choose a template below and customize to make it yours</p>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No templates available in this category yet.</p>
          </div>
        ) : (
          <Row>
            {templates.map((template, index) => (
              <Col key={index} xs={6} sm={6} md={4} lg={3}>
                <Card
                  className="mb-4 h-100 border-0 ws-img-clickable"
                  onClick={() => navigate(`/products/template/${template.productId}`)}
                >
                  <div className="position-relative">
                    <Card.Img
                      variant="top"
                      src={template.previewImageUrls?.[0]}
                      alt={template.name}
                      className="img-fluid"
                    />

                    {/* Product Name + Catalog Name Overlay */}
                    <div className="position-absolute bottom-0 start-50 translate-middle-x" style={{ zIndex: 1 }}>
                      <div
                        className="d-flex flex-column align-items-center text-nowrap text-center bg-light text-dark px-2 py-1 rounded shadow-sm"
                        style={{ fontSize: '0.9rem', opacity: 0.9 }}
                      >
                        <span className="fw-bold fst-italic">
                          {template.name}
                        </span>
                        {template.catalogItemName && (
                          <span className="text-muted mb-1">
                            <small>{template.catalogItemName}</small>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </>
  );
};

export default TemplateGroup;
